import React, { lazy, Suspense } from 'react'

import EmailSendingDomains from 'components/Conversations/EmailSendingDomains'
import { canReadManageTeammates } from 'utils/Access'

const AccountPage = lazy(() => import('components/AccountPage'))
const BillingPage = lazy(() => import('layouts/Billing'))
const ChatWidgetPage = lazy(() => import('layouts/WidgetSettings'))
const SecurityPage = lazy(() => import('components/SecurityPage'))
const AuthenticationRouter = lazy(() =>
  import('components/SecurityPage/AuthenticationRouter')
)
const SamlSettings = lazy(() => import('components/SecurityPage/SamlSettings'))
const DataPrivacyLayout = lazy(() => import('components/DataPrivacy'))
const CompanyPage = lazy(() => import('components/CompanyPage'))
const HelpLayout = lazy(() => import('components/Help'))
const RoutingLayout = lazy(() => import('components/Routing'))
const BotSettings = lazy(() => import('pages/Bot'))
const MeetingsPage = lazy(() => import('components/MeetingsPage'))
const InboxPage = lazy(() => import('layouts/Inbox'))
const ManageTeammates = lazy(() => import('pages/ManageTeammates'))
const TeamsLayout = lazy(() => import('components/Teams'))
const Integrations = lazy(() => import('components/Integrations'))
const Notifications = lazy(() => import('components/Notifications'))
const DealAdminPage = lazy(() => import('components/DealAdminPage'))
const EmailSettingsPage = lazy(() => import('components/EmailSettingsPage'))
const AuditPage = lazy(() => import('pages/AuditLog'))
const ProfileSettingsPage = lazy(() => import('components/ProfileSettingsPage'))
const SlackAuth = lazy(() => import('components/AuthRedirect/SlackAuth'))
const SalesloftAuth = lazy(() =>
  import('components/AuthRedirect/SalesloftAuth')
)
const SalesforceAuth = lazy(() =>
  import('components/AuthRedirect/SalesforceAuth')
)
const MicrosoftDynamicsAuth = lazy(() =>
  import('components/AuthRedirect/MicrosoftDynamicsAuth')
)
const SalesforceSandboxAuth = lazy(() =>
  import('components/AuthRedirect/SalesforceSandboxAuth')
)
const PardotAuth = lazy(() => import('components/AuthRedirect/PardotAuth'))
const HubspotAuth = lazy(() => import('components/AuthRedirect/HubspotAuth'))
const GoogleCalendarAuth = lazy(() =>
  import('components/AuthRedirect/CalendarAuth/GoogleCalendarAuth')
)
const MicrosoftCalendarAuth = lazy(() =>
  import('components/AuthRedirect/CalendarAuth/MicrosoftCalendarAuth')
)
const MicrosoftAdminCalendarAuth = lazy(() =>
  import('components/AuthRedirect/CalendarAuth/MicrosoftAdminCalendarAuth')
)
const ZoomAuth = lazy(() =>
  import('components/AuthRedirect/CalendarAuth/ZoomAuth')
)
const ZendeskAuth = lazy(() => import('components/AuthRedirect/ZendeskAuth'))

const ConversationsPage = lazy(() => import('components/Conversations'))
const GPTFeatures = lazy(() => import('components/GPT'))
const SavedRepliesPage = lazy(() =>
  import('components/Conversations/SavedRepliesPage')
)
const AdvancedConversationSettings = lazy(() =>
  import('components/Conversations/AdvancedConversationSettings')
)
const WorspacesManagement = lazy(() => import('components/Workspaces'))
const WorspacesPage = lazy(() => import('pages/Workspaces'))
const UserLogs = lazy(() => import('components/UserLogs'))
const ProspectorPage = lazy(() => import('components/ProspectorPage'))

export const RESTRICTED_PATH = '/account'
export const getDefaultPath = () => {
  return canReadManageTeammates() ? '/team' : RESTRICTED_PATH
}

const FallbackDiv = () => <div />

export const PAGES = [
  {
    path: '/audit',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <AuditPage />
      </Suspense>
    ),
  },
  {
    path: '/account',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <AccountPage />
      </Suspense>
    ),
  },
  {
    path: '/gpt',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <GPTFeatures />
      </Suspense>
    ),
  },
  {
    path: '/billing',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <BillingPage />
      </Suspense>
    ),
    requiredAccess: 'hasBilling',
  },
  {
    path: '/widget',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <ChatWidgetPage />
      </Suspense>
    ),
    requiredAccess: 'hasWidget',
  },
  {
    path: '/security/saml',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SamlSettings />
      </Suspense>
    ),
    requiredAccess: 'hasSecurity',
  },
  {
    path: '/security',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SecurityPage />
      </Suspense>
    ),
    requiredAccess: 'hasSecurity',
  },
  {
    path: '/authentication',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <AuthenticationRouter />
      </Suspense>
    ),
  },
  {
    path: '/consent',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <DataPrivacyLayout />
      </Suspense>
    ),
    requiredAccess: 'hasDataPrivacy',
  },
  {
    path: '/bot',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <BotSettings />
      </Suspense>
    ),
    requiredAccess: 'hasBot',
  },
  {
    path: '/meetings',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <MeetingsPage />
      </Suspense>
    ),
  },
  {
    path: '/company',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <CompanyPage />
      </Suspense>
    ),
    requiredAccess: 'hasCompany',
  },
  {
    path: '/help',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <HelpLayout />
      </Suspense>
    ),
  },
  {
    path: '/rules',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <RoutingLayout />
      </Suspense>
    ),
    requiredAccess: 'hasConvoRouting',
  },
  {
    path: '/inbox',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <InboxPage />
      </Suspense>
    ),
    requiredAccess: 'hasInboxes',
  },
  {
    path: '/team',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <ManageTeammates />
      </Suspense>
    ),
    requiredAccess: 'hasManageTeammates',
  },
  {
    path: '/teams',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <TeamsLayout />
      </Suspense>
    ),
    requiredAccess: 'hasTeams',
  },
  {
    path: '/integrations',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <Integrations />
      </Suspense>
    ),
  },
  {
    path: '/deal-admin',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <DealAdminPage />
      </Suspense>
    ),
    requireAdminOrg: true,
  },
  {
    path: '/notifications',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <Notifications />
      </Suspense>
    ),
    requireWebApp: true,
  },
  {
    path: '/email',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <EmailSettingsPage />
      </Suspense>
    ),
  },
  {
    path: '/slack/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SlackAuth />
      </Suspense>
    ),
  },
  {
    path: '/salesloft/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SalesloftAuth />
      </Suspense>
    ),
  },
  {
    path: '/salesforce/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SalesforceAuth />
      </Suspense>
    ),
  },
  {
    path: '/salesforce-sandbox/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SalesforceSandboxAuth />
      </Suspense>
    ),
  },
  {
    path: '/pardot/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <PardotAuth />
      </Suspense>
    ),
  },
  {
    path: '/hubspot/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <HubspotAuth />
      </Suspense>
    ),
  },
  {
    path: '/microsoft-dynamics/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <MicrosoftDynamicsAuth />
      </Suspense>
    ),
  },
  {
    path: '/google-calendar/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <GoogleCalendarAuth />
      </Suspense>
    ),
  },
  {
    path: '/office-365-calendar/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <MicrosoftCalendarAuth />
      </Suspense>
    ),
  },
  {
    path: '/office-365-admin-consent/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <MicrosoftAdminCalendarAuth />
      </Suspense>
    ),
  },
  {
    path: '/zoom/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <ZoomAuth />
      </Suspense>
    ),
  },
  {
    path: '/zendesk/auth',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <ZendeskAuth />
      </Suspense>
    ),
  },
  {
    path: '/conversations',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <ConversationsPage />
      </Suspense>
    ),
    requiredAccess: 'hasConversations',
  },
  {
    path: '/conversations/advanced',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <AdvancedConversationSettings />
      </Suspense>
    ),
  },
  {
    path: '/profile',
    Component: () => (
      <Suspense fallback={FallbackDiv()}>
        <ProfileSettingsPage />
      </Suspense>
    ),
  },
  {
    path: '/replies',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <SavedRepliesPage />
      </Suspense>
    ),
  },
  {
    path: '/workspaces',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <WorspacesManagement />
      </Suspense>
    ),
    requireWorkspaces: true,
  },
  {
    path: '/workspaces2',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <WorspacesPage />
      </Suspense>
    ),
    requireWorkspaces: true,
  },
  {
    path: '/dkim',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <EmailSendingDomains />
      </Suspense>
    ),
  },
  {
    path: '/user-logs',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <UserLogs />
      </Suspense>
    ),
  },
  {
    path: '/prospector',
    Component: () => (
      <Suspense fallback={FallbackDiv}>
        <ProspectorPage />
      </Suspense>
    ),
  },
]
